import React, { createContext, useMemo, useReducer } from 'react';

import { FastlaneInitialState } from 'Utilities/fastlane';
import { isApplicationNonProd, isSSR } from 'Utilities/helpers';
import { safeLocalStorage } from 'Utilities/storages';

type AdditionalState = {
  agentAvailability: {
    liveChatAvailable: boolean;
  } | null;
  appInit: number;
  zendeskWidgetState: {
    show: boolean;
  };
} & Partial<FastlaneInitialState>;

type ConfigState = FastlaneInitialState & AdditionalState;

export enum ConfigActionType {
  CONFIG_UPDATE = 'CONFIG_UPDATE',
  UPDATE_OVERIDE_FEATURE_FLAGS = 'UPDATE_OVERIDE_FEATURE_FLAGS',
  ZENDESK_UPDATE = 'ZENDESK_UPDATE'
}

type ConfigAction = {
  payload: Partial<ConfigState> | Partial<ConfigState['zendeskWidgetState']>;
  type: ConfigActionType;
}

type ConfigContext = {
  configActions: {
    overrideFeatureFlags: (fields: ConfigState['featureFlags']) => void;
    resetFeatureFlag: (fields: ConfigState['featureFlags']) => void;
    resetFeatureFlags: () => void;
    update: (fields: Partial<ConfigState>) => void;
    updateZendesk: (fields: Partial<ConfigState['zendeskWidgetState']>) => void;
  };
  configState: ConfigState | AdditionalState;
}

type ConfigProviderProps = {
  children: React.ReactNode;
  value: FastlaneInitialState;
}

// Export state available outside of React
const ConfigStateStatic = {
  state: {},
};

const initialActions = {
  overrideFeatureFlags: () => {},
  resetFeatureFlag: () => {},
  resetFeatureFlags: () => {},
  update: () => {},
  updateZendesk: () => {},
};

const initialState = {
  agentAvailability: null,
  appInit: Date.now(),
  zendeskWidgetState: {
    show: false,
  },
};

const ConfigContext = createContext<ConfigContext>({ configActions: initialActions, configState: initialState });

export const reducer = (state: ConfigState, { payload, type }: ConfigAction) => {
  let newState: ConfigState;
  switch (type) {
  case ConfigActionType.CONFIG_UPDATE:
    newState = {
      ...state,
      ...payload,
    };
    break;
  case ConfigActionType.ZENDESK_UPDATE:
    newState = {
      ...state,
      zendeskWidgetState: {
        ...state.zendeskWidgetState,
        ...payload,
      },
    };
    break;
  case ConfigActionType.UPDATE_OVERIDE_FEATURE_FLAGS:
    newState = {
      ...state,
      featureFlags: {
        ...payload,
      } as ConfigState['featureFlags'],
    };
    break;
  default:
    window?.Sentry?.captureException?.(new Error('Unexpected use of default switch case'), {
      level: 'warning',
    });
    newState = state;
  }

  return newState;
};

export const saveFeatureFlagsToStorage = (featureFlags: ConfigState['featureFlags']) => {
  safeLocalStorage.setItem('featureFlags', featureFlags);
};

const updateStaticConfigState = (state: ConfigState) => {
  ConfigStateStatic.state = Object.freeze({
    ...state,
  });
};

const ConfigProvider = ({ children, value }: ConfigProviderProps) => {
  const overrideFlags = (isApplicationNonProd
    && !isSSR()
    && safeLocalStorage.getItem('featureFlags')) || {};

  const state = {
    ...initialState,
    ...value,
    featureFlags: {
      ...value.featureFlags,
      ...overrideFlags,
    },
  };

  const [configState, dispatch] = useReducer(reducer, state);

  const configActions = useMemo(() => {
    const originalFeatureFlags = { ...value.featureFlags };

    return {
      overrideFeatureFlags: (fields: ConfigState['featureFlags']) => {
        if (fields) {
          const featureFlagsToBeSet = {
            ...safeLocalStorage.getItem('featureFlags'),
            ...fields,
          };

          saveFeatureFlagsToStorage(featureFlagsToBeSet);

          dispatch({
            payload: {
              ...originalFeatureFlags,
              ...featureFlagsToBeSet,
            },
            type: ConfigActionType.UPDATE_OVERIDE_FEATURE_FLAGS,
          });
        }
      },
      resetFeatureFlag: (fields: ConfigState['featureFlags']) => {
        if (fields) {
          const featureFlagKey = Object.keys(fields)[0];
          const currentFeatureFlagsInStorage = safeLocalStorage.getItem('featureFlags');

          const { ...newFeatureFlagsInStorage } = currentFeatureFlagsInStorage;
          delete newFeatureFlagsInStorage[featureFlagKey];

          saveFeatureFlagsToStorage(newFeatureFlagsInStorage);

          dispatch({
            payload: {
              ...originalFeatureFlags,
              ...newFeatureFlagsInStorage,
            },
            type: ConfigActionType.UPDATE_OVERIDE_FEATURE_FLAGS,
          });
        }
      },
      resetFeatureFlags: () => {
        saveFeatureFlagsToStorage({});

        dispatch({
          payload: {
            ...originalFeatureFlags,
          },
          type: ConfigActionType.UPDATE_OVERIDE_FEATURE_FLAGS,
        });
      },
      update: (fields: Partial<ConfigState>) => {
        if (fields) {
          dispatch({ payload: fields, type: ConfigActionType.CONFIG_UPDATE });
        }
      },
      updateZendesk: (fields: Partial<ConfigState['zendeskWidgetState']>) => {
        if (fields) {
          // Stop the update from blocking the main thread with internal widget processing
          setTimeout(() => dispatch({ payload: fields, type: ConfigActionType.ZENDESK_UPDATE }), 0);
        }
      },
    };
  }, [value.featureFlags]);

  updateStaticConfigState(configState);

  return (
    <ConfigContext.Provider
      value={{
        configActions,
        configState,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
};

export {
  ConfigContext,
  ConfigProvider,
  ConfigStateStatic,
};
